import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import DataTable from '../../components/admin/DataTable';
import AIFormAssistant from '../../components/admin/AIFormAssistant';
import ImageUpload from '../../components/admin/ImageUpload';
import {
  fetchAdminAgents,
  createAgent,
  updateAgent,
  deleteAgent,
  toggleAgentStatus,
  selectAdminAgents,
  selectAdminAgentsLoading,
  selectAdminError
} from '../../redux/slices/adminSlice';

const AdminAgents = () => {
  const dispatch = useDispatch();
  const agents = useSelector(selectAdminAgents);
  const loading = useSelector(selectAdminAgentsLoading);
  const error = useSelector(selectAdminError);

  const [showModal, setShowModal] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    avatar: '',
    bio: '',
    specialization: '',
    licenseNumber: '',
    experience: '',
    companyName: '',
    languages: '',
    rating: '',
    reviewCount: '',
    totalSales: '',
    serviceAreas: '',
    isActive: true,
    isFeatured: false
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [performanceData, setPerformanceData] = useState({});

  useEffect(() => {
    dispatch(fetchAdminAgents());
  }, [dispatch]);

  useEffect(() => {
    loadPerformanceData();
  }, [agents]);

  const loadPerformanceData = () => {
    const mockData = {};
    agents.forEach(agent => {
      mockData[agent.id] = {
        propertiesSold: Math.floor(Math.random() * 50) + 10,
        totalRevenue: Math.floor(Math.random() * 500000) + 100000,
        clientSatisfaction: (Math.random() * 2 + 3).toFixed(1),
        avgDaysOnMarket: Math.floor(Math.random() * 30) + 15,
        conversionRate: (Math.random() * 30 + 15).toFixed(1)
      };
    });
    setPerformanceData(mockData);
  };

  const handleAIQuery = async (query) => {
    if (query.toLowerCase().includes('agent performance') || query.toLowerCase().includes('analytics')) {
      const totalAgents = agents.length;
      const activeAgents = agents.filter(a => a.isActive).length;
      const topPerformer = agents.length > 0 
        ? agents.reduce((top, agent) => 
            (performanceData[agent.id]?.propertiesSold || 0) > (performanceData[top.id]?.propertiesSold || 0) ? agent : top
          , agents[0])
        : {};

      return `📊 **AGENT PERFORMANCE ANALYTICS**

**Team Overview:**
• Total Agents: ${totalAgents}
• Active Agents: ${activeAgents}
• Verification Rate: ${totalAgents > 0 ? Math.floor((agents.filter(a => a.isVerified).length / totalAgents) * 100) : 0}%

**Top Performer:** ${topPerformer?.firstName} ${topPerformer?.lastName}
• Properties Sold: ${performanceData[topPerformer?.id]?.propertiesSold || 0}
• Revenue Generated: ₹${performanceData[topPerformer?.id]?.totalRevenue?.toLocaleString("en-IN", {
  style: "currency",
  currency: "INR"
}) || '0'}
• Client Rating: ${performanceData[topPerformer?.id]?.clientSatisfaction || '0'}/5.0

**Team Metrics:**
📈 Average Properties/Agent: ${totalAgents > 0 ? Math.floor(Object.values(performanceData).reduce((sum, data) => sum + (data.propertiesSold || 0), 0) / totalAgents) : 0}
💰 Total Team Revenue: ₹${Object.values(performanceData).reduce((sum, data) => sum + (data.totalRevenue || 0), 0).toLocaleString("en-IN", {
  style: "currency",
  currency: "INR"
})}
⭐ Average Satisfaction: ${totalAgents > 0 ? (Object.values(performanceData).reduce((sum, data) => sum + parseFloat(data.clientSatisfaction || 0), 0) / totalAgents).toFixed(1) : '0'}/5.0

**AI Recommendations:**
🎯 Focus training on: Lead conversion techniques
📞 Schedule follow-up coaching for underperforming agents
🏆 Implement performance incentive program
📧 Launch client satisfaction improvement initiative`;
    }

    if (query.toLowerCase().includes('agent bio') || query.toLowerCase().includes('generate bio')) {
      return `✨ **AI AGENT PROFILE GENERATOR**

Based on ${formData.firstName || 'Agent'}'s profile, here's a professional bio:

**${formData.firstName} ${formData.lastName} - ${formData.specialization || 'Real Estate Specialist'}**

${formData.firstName || 'This agent'} brings ${formData.experience || '5+ years'} of dedicated experience to the real estate industry, specializing in ${formData.specialization || 'residential properties'}. Licensed professional ${formData.licenseNumber ? `(License: ${formData.licenseNumber})` : ''} with a proven track record of excellence.

**Professional Expertise:**
• ${formData.specialization || 'Residential Sales'} specialist
• Market analysis and property valuation
• Negotiation and closing expertise
• Client relationship management
• ${formData.languages ? `Multilingual: ${formData.languages}` : 'English communication'}

**Service Areas:**
${formData.serviceAreas || 'Greater metropolitan area and surrounding communities'}

**Contact Information:**
📧 ${formData.email || 'Professional email available'}
📞 ${formData.phone || 'Direct phone line available'}
🌐 Licensed Real Estate Professional

*"Your success is my priority. Let's make your real estate dreams a reality."*

Would you like me to adjust the tone or focus on specific expertise areas?`;
    }

    return `🤖 **AGENT MANAGEMENT AI**

I can help you with:
• 📊 Performance analytics and insights
• ✨ Professional bio generation
• 🎯 Territory optimization
• 📈 Market analysis and trends
• 👥 Team performance comparisons
• 🏆 Recognition and improvement plans

What specific aspect would you like me to analyze?`;
  };

  const handleAIProfileGeneration = () => {
    setAiSuggestions("🤖 AI is analyzing agent profile and generating professional content...");
    
    setTimeout(() => {
      const suggestions = {
        bio: `${formData.firstName} ${formData.lastName} is a dedicated real estate professional with ${formData.experience || '5+'} years of experience in the industry. Specializing in ${formData.specialization || 'residential properties'}, ${formData.firstName} is committed to providing exceptional service and helping clients achieve their real estate goals. Licensed professional with a proven track record of successful transactions and satisfied clients.`,
        specializations: [
          'Residential Sales',
          'Luxury Properties',
          'First-Time Buyers',
          'Investment Properties',
          'Commercial Real Estate',
          'Relocation Services'
        ],
        serviceAreas: [
          'Downtown Metropolitan',
          'Suburban Communities',
          'Waterfront Properties',
          'Historic Districts',
          'New Developments'
        ],
        achievements: [
          'Top Producer Award Winner',
          'Client Satisfaction Excellence',
          'Million Dollar Sales Club',
          'Community Service Recognition',
          'Professional Development Leader'
        ],
        marketingTagline: `"${formData.firstName} ${formData.lastName} - Your trusted partner in real estate success. Experience. Integrity. Results."`,
        socialMediaBio: `Licensed Real Estate Professional | ${formData.specialization || 'Residential Specialist'} | Helping clients achieve their property goals | ${formData.experience || '5+'} years experience`,
        emailSignature: `${formData.firstName} ${formData.lastName}\n${formData.specialization || 'Real Estate Professional'}\nLicense: ${formData.licenseNumber || 'Licensed Professional'}\n📧 ${formData.email || 'Email'} | 📞 ${formData.phone || 'Phone'}`
      };
      
      setAiSuggestions(suggestions);
    }, 2000);
  };

  const applyAISuggestions = () => {
    if (aiSuggestions && typeof aiSuggestions === 'object') {
      setFormData(prev => ({
        ...prev,
        bio: aiSuggestions.bio,
        specialization: aiSuggestions.specializations?.slice(0, 3).join(', ') || prev.specialization,
        serviceAreas: aiSuggestions.serviceAreas?.slice(0, 3).join(', ') || prev.serviceAreas,
        achievements: aiSuggestions.achievements?.slice(0, 3).join(', ') || prev.achievements,
        marketingTagline: aiSuggestions.marketingTagline,
        socialMediaBio: aiSuggestions.socialMediaBio
      }));
      setAiSuggestions(null);
    }
  };

  const agentColumns = [
    {
      key: 'verified',
      label: '✓',
      render: (verified) => (
        <span className={`text-xl ${verified ? 'text-green-500' : 'text-gray-300'}`}>
          {verified ? '✓' : '○'}
        </span>
      )
    },
    {
      key: 'avatar',
      label: 'Photo',
      render: (avatar, agent) => (
        <div className="flex items-center">
          <img
            src={avatar || '/placeholder-agent.jpg'}
            alt={`${agent.firstName} ${agent.lastName}`}
            className="h-10 w-10 rounded-full object-cover"
          />
        </div>
      )
    },
    {
      key: 'name',
      label: 'Name',
      render: (_, agent) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {agent.firstName} {agent.lastName}
          </div>
          <div className="text-sm text-gray-500">{agent.specialization}</div>
        </div>
      )
    },
    {
      key: 'contact',
      label: 'Contact',
      render: (_, agent) => (
        <div>
          <div className="text-sm text-gray-900">{agent.email}</div>
          <div className="text-sm text-gray-500">{agent.phone}</div>
        </div>
      )
    },
    {
      key: 'experience',
      label: 'Experience',
      render: (experience) => `${experience || 0} years`
    },
    {
      key: 'status',
      label: 'Status',
      render: (_, agent) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          agent.isActive 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {agent.isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      key: 'performance',
      label: 'Performance',
      render: (_, agent) => {
        const perf = performanceData[agent.id];
        return perf ? (
          <div className="text-sm">
            <div>{perf.propertiesSold} sales</div>
            <div className="text-gray-500">${perf.totalRevenue.toLocaleString("en-IN", {
  style: "currency",
  currency: "INR"
})}</div>
          </div>
        ) : (
          <div className="text-sm text-gray-400">No data</div>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, agent) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleEdit(agent)}
            className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
          >
            Edit
          </button>
          <button
            onClick={() => handleToggleStatus(agent)}
            className={`text-sm font-medium ${
              agent.isActive 
                ? 'text-red-600 hover:text-red-900' 
                : 'text-green-600 hover:text-green-900'
            }`}
          >
            {agent.isActive ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => handleDelete(agent.id)}
            className="text-red-600 hover:text-red-900 text-sm font-medium"
          >
            Delete
          </button>
        </div>
      )
    }
  ];

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      avatar: '',
      bio: '',
      specialization: '',
      licenseNumber: '',
      experience: '',
      companyName: '',
      languages: '',
      rating: '',
      reviewCount: '',
      totalSales: '',
      serviceAreas: '',
      isActive: true,
      isFeatured: false
    });
    setErrors({});
    setAiSuggestions(null);
  };

  const handleModalOpen = (agent = null) => {
    if (agent) {
      setEditingAgent(agent);
      setFormData(agent);
    } else {
      setEditingAgent(null);
      resetForm();
    }
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingAgent(null);
    resetForm();
  };

  const handleEdit = (agent) => {
    handleModalOpen(agent);
  };

  const handleToggleStatus = async (agent) => {
    try {
      await dispatch(toggleAgentStatus(agent.id)).unwrap();
      dispatch(fetchAdminAgents());
    } catch (error) {
      console.error('Error toggling agent status:', error);
    }
  };

  const handleDelete = async (agentId) => {
    if (window.confirm('Are you sure you want to delete this agent? This action cannot be undone.')) {
      try {
        await dispatch(deleteAgent(agentId)).unwrap();
        dispatch(fetchAdminAgents());
      } catch (error) {
        console.error('Error deleting agent:', error);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone is required';
    }
    
    if (!formData.licenseNumber?.trim()) {
      newErrors.licenseNumber = 'License number is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      if (editingAgent) {
        await dispatch(updateAgent({ id: editingAgent.id, ...formData })).unwrap();
      } else {
        await dispatch(createAgent(formData)).unwrap();
      }
      
      handleModalClose();
      dispatch(fetchAdminAgents());
    } catch (error) {
      console.error('Error saving agent:', error);
      setErrors({ submit: error.message || 'Failed to save agent' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || 
                         (filterType === 'active' && agent.isActive) ||
                         (filterType === 'inactive' && !agent.isActive) ||
                         (filterType === 'featured' && agent.isFeatured);
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 shadow rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Agent Management</h1>
            <p className="mt-2 text-indigo-100">
              AI-powered agent management with performance analytics
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => handleModalOpen()}
            className="inline-flex items-center px-6 py-3 border border-transparent shadow-sm text-base font-medium rounded-md text-indigo-600 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Agent
          </motion.button>
        </div>

        {/* Stats Cards */}
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-3xl">👥</span>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-indigo-100 truncate">All agents</dt>
                  <dd className="text-2xl font-bold text-white">{agents.length}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-3xl">✅</span>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-indigo-100 truncate">Active</dt>
                  <dd className="text-2xl font-bold text-white">
                    {agents.filter(a => a.isActive).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-3xl">⭐</span>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-indigo-100 truncate">Featured</dt>
                  <dd className="text-2xl font-bold text-white">
                    {agents.filter(a => a.isFeatured).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-3xl">📊</span>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-indigo-100 truncate">Avg Rating</dt>
                  <dd className="text-2xl font-bold text-white">4.8</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Search agents
            </label>
            <input
              id="search"
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="filter" className="block text-sm font-medium text-gray-700 mb-1">
              Filter by status
            </label>
            <select
              id="filter"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="all">All Agents</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="featured">Featured</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quick actions
            </label>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilterType('active')}
                className="flex-1 px-3 py-2 text-xs font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200"
              >
                Active
              </button>
              <button
                onClick={() => setFilterType('featured')}
                className="flex-1 px-3 py-2 text-xs font-medium text-purple-700 bg-purple-100 rounded-md hover:bg-purple-200"
              >
                Featured
              </button>
            </div>
          </div>
        </div>
      </div>

      

      {/* Data Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Agents ({filteredAgents.length})
          </h3>
        </div>
        <DataTable
          columns={agentColumns}
          data={filteredAgents}
          loading={loading}
          emptyMessage="No agents found. Add your first agent!"
        />
      </div>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          >
            <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {editingAgent ? 'Edit Agent' : 'Add New Agent'}
                  </h3>
                  <button
                    onClick={handleModalClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* AI Content Generation */}
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-md font-medium text-purple-900">
                        🤖 AI-enhanced professional profile creation
                      </h4>
                      <button
                        type="button"
                        onClick={handleAIProfileGeneration}
                        className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm hover:bg-purple-700 transition-colors"
                      >
                        Generate Profile
                      </button>
                    </div>
                    
                    {aiSuggestions && (
                      <div className="space-y-4">
                        {typeof aiSuggestions === 'string' ? (
                          <p className="text-sm text-purple-700">{aiSuggestions}</p>
                        ) : (
                          <div className="bg-white p-4 rounded-lg border">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="font-medium text-gray-900">
                                Professional content generation for agent profiles
                              </h5>
                              <button
                                type="button"
                                onClick={applyAISuggestions}
                                className="px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors"
                              >
                                Apply Suggestions
                              </button>
                            </div>
                            
                            <div className="space-y-3 text-sm">
                              <div>
                                <strong>Generated Bio:</strong>
                                <p className="text-gray-600 mt-1">{aiSuggestions.bio}</p>
                              </div>
                              <div>
                                <strong>Marketing Tagline:</strong>
                                <p className="text-gray-600 mt-1">{aiSuggestions.marketingTagline}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Form Fields */}
                  <div className="grid grid-cols-1 mx-auto gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                          errors.firstName ? 'border-red-300' : ''
                        }`}
                        placeholder="Enter first name"
                      />
                      {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                          errors.lastName ? 'border-red-300' : ''
                        }`}
                        placeholder="Enter last name"
                      />
                      {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                          errors.email ? 'border-red-300' : ''
                        }`}
                        placeholder="Enter email address"
                      />
                      {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                          errors.phone ? 'border-red-300' : ''
                        }`}
                        placeholder="Enter phone number"
                      />
                      {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Specialization
                      </label>
                      <select
                        value={formData.specialization}
                        onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="">Select specialization</option>
                        <option value="residential">Residential Sales</option>
                        <option value="luxury">Luxury Properties</option>
                        <option value="commercial">Commercial Real Estate</option>
                        <option value="investment">Investment Properties</option>
                        <option value="first-time-buyers">First-Time Buyers</option>
                        <option value="relocation">Relocation Services</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        License Number *
                      </label>
                      <input
                        type="text"
                        value={formData.licenseNumber}
                        onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                        className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                          errors.licenseNumber ? 'border-red-300' : ''
                        }`}
                        placeholder="Enter license number"
                      />
                      {errors.licenseNumber && <p className="mt-1 text-sm text-red-600">{errors.licenseNumber}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Years of Experience
                      </label>
                      <input
                        type="number"
                        value={formData.experience}
                        onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Enter years of experience"
                        min="0"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={formData.companyName}
                        onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Enter company name"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Professional Bio
                      </label>
                      <textarea
                        value={formData.bio}
                        onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                        rows={4}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Enter professional bio..."
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <ImageUpload
                        label="Agent Photo"
                        value={formData.avatar}
                        onChange={(url) => setFormData({ ...formData, avatar: url })}
                        accept="image/*"
                        required={!editingAgent}
                      />
                    </div>

                    <div className="sm:col-span-2 flex items-center space-x-6">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.isActive}
                          onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-900">
                          Active Agent
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.isFeatured}
                          onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label className="ml-2 block text-sm text-gray-900">
                          Featured Agent
                        </label>
                      </div>
                    </div>
                  </div>

                  {errors.submit && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                      <p className="text-sm text-red-600">{errors.submit}</p>
                    </div>
                  )}

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={handleModalClose}
                      className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {isSubmitting ? 'Saving...' : editingAgent ? 'Update Agent' : 'Add Agent'}
                    </motion.button>
                  </div>
                </form>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminAgents;
